import React from 'react';
import moment from 'moment-jalaali';
import cloneDeep from 'lodash/cloneDeep';
import upperFirst from 'lodash/upperFirst';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import {
  addScoreToMetric,
  makeBucket,
  makeFiltersFromFields,
  makeFiltersFromSeries,
} from './analyseUtils';
import { englishNumber, isANumber, randomNumber } from 'utils/number';
import { isPrefix, removeDuplicates } from 'utils/happyFunctions';
import { endOfDate, jMonthIndex, startOfDate } from 'utils/date';
import { IDataContext, withDataContext } from 'store/context/DataContext';
import { NumberOrNull, StringOrNull } from 'store/entity/types';
import { appDispatch, RootState } from 'store/store';
import {
  IProviderItemView,
  IProviderItemViewWithData,
  IProviders,
  ITags,
  ReportViewType,
} from 'store/providers/providerTypes';
import { IOption } from 'components/Select/Select';
import ChartSeries from './ChartSeries';
import SurveyChart from './SurveyChart';
import ChartSetting from './ChartSetting';
import { display } from 'components/ErrorView/ErrorDialog';
import notify from 'components/Notify';
import LoadingCircleIndicator from 'components/LoadingCircleIndicator';
import {
  GridContainer,
  GridReportBox,
  ListContainer,
  ReportBox,
  ViewerBox,
} from './styles';
import MergedChart from '../MergedChart/MergedChart';
import { getTemplateReportMark } from '../Report/PrintTaskReport/PrintTaskReport';
import TaskReport from '../Report/TaskReport/TaskReport';
import { isEqual } from 'lodash';
import {
  getAllProviders,
  getAllProvidersFn,
  requireProviderDocuments,
  requireProviderDocumentsFn,
  requireProviderTags,
  updateSurveyReport,
  updateSurveyReportFn,
} from 'store/providers/providerSlice';
import { IAnalyticsQuery } from 'store/entity/IAnalyticsQuery';
import {
  ChartTypes,
  DashboardLink,
  ICheckPoint,
  IMultipleChoiceQuestionView,
  IProviderSurveyReport,
  IQuestionReportField,
  IReportField,
  IReportView,
  IScoreRangeReportField,
  ISeries,
  ISurveyItem,
  ISurveyReportMap,
  ISurveyReports,
  ISurveyResponseView,
  ISurveys,
  ISurveyView,
  ITagReportField,
  ITimeInfo,
  SeriesOrder,
  SeriesType,
  TimeScale,
} from 'store/survey/surveyTypes';
import { IAnalyticResponse } from 'store/entity/IAnalyticResponse';
import { IExportResponsesQuery } from 'store/entity/IExportResponsesQuery';
import { ITask } from 'store/tasks/tasksType';
import { IUser } from 'store/users/usersType';
import { ICompany } from 'store/companies/companiesTypes';
import { IOpenItem } from 'store/entity/IOpenItem';
import { IQueryFilter } from 'store/entity/IQueryFilter';
import { IQueryBucket } from 'store/entity/IQueryBucket';
import { IQueryMetric } from 'store/entity/IQueryMetric';
import {
  AutoRolePermissionSchemeIndex,
  isCompanyOwnerOrSupport,
} from 'store/companies/companiesUtils';
import {
  cumulativePercentageReset,
  cumulativePercentageResetFn,
  exportResponses,
  exportResponsesFn,
  generateReports,
  generateReportsFn,
  getAnalyse,
  getAnalyseFn,
  getAnalyseList,
  getReportByIds,
  getReports,
  removeReport,
  removeReportFn,
  requireAllCheckpoints,
  requireAllCheckpointsFn,
  requireAllSurveys,
  requireAllSurveysFn,
  requireCheckPoints,
  saveReport,
  saveReportFn,
  searchMainReports,
  searchMainReportsFn,
} from 'store/survey/surveySlice';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';
import {
  getCompanyFn,
  getCompanyHandler,
} from 'store/companies/companiesSlice';
import { getTaskReport, getTaskReportFn } from 'store/tasks/tasksSlice';
import { ITemplateReportMark } from 'store/templates/templatesSlice';
import i18n, {
  getCalendarLocal,
  getLangBaseOnLocal,
  languageDirection,
} from '../../i18n';
import { ILocal } from './BaseDatePicker';
import { conditionMatchesAnswer } from '../SurveyResponses/ResponseForm/QuestionUtils';
import { TReportField } from './ReportFields';
import { responseListQuestionFields } from './SeriesEditor';

moment.loadPersian({ dialect: 'persian-modern' });

type RequestData = Array<{
  index: number;
  reportId: string;
  ownerToken?: string;
  surveyToken?: string;
  reportToken?: string;
  requestQuery: IAnalyticsQuery;
  type?: 'task' | 'survey' | 'responseList';
}>;

type CustomReport = {
  fields: Array<IReportField>;
  series: ISeries;
  splitOnTime: boolean;
  similarPastYearPeriodLength?: number;
  totalTime: boolean;
  timeScale: TimeScale;
  originalTimeScale: TimeScale;
  timeLength: string;
  surveyId?: number;
  reportId?: number;
};

type DispatchToProps = {
  deleteReport: (
    reportId: number,
    surveyToken?: string,
    ownerToken?: string,
    reportToken?: string
  ) => ReturnType<typeof removeReportFn>;
  getAllProviders: (
    providersId: number[]
  ) => ReturnType<typeof getAllProvidersFn>;
  requireSeveralUser: (
    userIds: number[]
  ) => ReturnType<typeof requireSeveralUserFn>;
  getAnalyse: (
    dataQuery: IAnalyticsQuery,
    surveyToken?: string,
    ownerToken?: string,
    reportToken?: string
  ) => ReturnType<typeof getAnalyseFn>;
  requireAllCheckpoints: (
    reportsIds: number[],
    reportToken?: string,
    ownerToken?: string
  ) => ReturnType<typeof requireAllCheckpointsFn>;
  getReports: (surveyToken: string) => any;
  requireCheckPoints: (
    surveyId?: number,
    reportId?: number,
    surveyToken?: string,
    ownerToken?: string,
    reportToken?: string
  ) => any;
  exportResponses: (
    dataQuery: IExportResponsesQuery,
    surveyToken?: string,
    ownerToken?: string,
    reportToken?: string
  ) => ReturnType<typeof exportResponsesFn>;
  requireReportsByIds: (reportIds: number[]) => any;
  requireAllSurveys: (
    surveysTokens: string[]
  ) => ReturnType<typeof requireAllSurveysFn>;
  requireTags: (providerId: number) => any;
  saveReport: (
    report: IReportView,
    isUpdate: boolean,
    surveyToken?: string,
    ownerToken?: string,
    reportToken?: string
  ) => ReturnType<typeof saveReportFn>;
  updateSurveyReportItem: (
    item: IProviderSurveyReport
  ) => ReturnType<typeof updateSurveyReportFn>;
  cumulativePercentageReset: (
    groupId: string
  ) => ReturnType<typeof cumulativePercentageResetFn>;
  generateReport: (
    questionId: number | undefined,
    options: any,
    surveyToken: string
  ) => ReturnType<typeof generateReportsFn>;
  getDocuments: (
    documentIds: number[]
  ) => ReturnType<typeof requireProviderDocumentsFn>;
  getCompany: (companyId: number) => ReturnType<typeof getCompanyFn>;
  getTaskReport: (
    companyId: number,
    templateReport: ITemplateReportMark,
    startDate: number,
    toDate: number,
    projectId: NumberOrNull,
    parentId: NumberOrNull,
    templateId: number,
    assigneeId: NumberOrNull,
    splitMonths: number,
    splitField: StringOrNull,
    fieldValues: { [key: string]: string }
  ) => ReturnType<typeof getTaskReportFn>;
};

type StateToProps = {
  surveys: ISurveys;
  tags: ITags | null;
  providers: IProviders;
  survey?: ISurveyView;
  viewingVersion?: string;
  tasks: { [p: number]: ITask };
  users: { [p: number]: IUser };
  company?: ICompany;
  providerItems: { [p: number]: IProviderItemViewWithData };
  currentUserCompanyRoles: number[];
  isOwner?: boolean;
  managingCompanyId?: number | null;
  thisUserId: number | undefined;
};

type OwnProps = {
  dataContext: IDataContext;
  reportItem?: IOpenItem;
  surveyId?: number;
  surveyItem?: ISurveyItem;
  extraContext?: string;
  extraFiltersIfNotSplit?: IQueryFilter[];
  autoLoad?: boolean;
  viewType?: ReportViewType;
  embedded?: boolean;
  baseDate: Date;
  onChangeBaseDate: (baseDate: Date) => void;
  extraTagIds?: number[];
  externalTagFilter?: number[];
  simple?: boolean;
  selectedCategorizedTag?: string;
};

type ComponentProps = OwnProps & DispatchToProps & StateToProps;
export interface IAnalyseState {
  [seriesIndex: number]: IAnalyticResponse | ISurveyResponseView[];
}

export interface IQueryState {
  [seriesIndex: number]: IAnalyticsQuery;
}

export type MapRequest = {
  [reportId: string]: {
    data: IQueryState;
    order: number;
  };
};

export type MapResponse = { [reportId: string]: IAnalyseState };

export interface ReportOverride {
  timeScale?: TimeScale;
  timeLength?: number;
  loading?: boolean;
  chartType?: ChartTypes;
  chartColor?: string;
  [key: number]: (string | number)[];
  overrideFilters?: boolean;
  order?: SeriesOrder;
}

type ComponentState = {
  editFields: Array<IReportField | IScoreRangeReportField>;
  editSeries: ISeries[];
  viewFields: Array<IReportField>;
  viewSeries: ISeries[];
  display: ReportViewType;
  collapsedCharts: number[];
  editing: boolean;
  editType?: string;
  loading: boolean;
  loadingAnalyse: boolean;
  mapResponse?: MapResponse;
  mapRequest?: MapRequest;
  allReports?: { [key: number]: IReportView };
  multipleRequest?: boolean;
  reportName: string;
  reportTags: IOption[];
  savingReport: boolean;
  selectedReport: NumberOrNull; // null for nothing, positive numbers for editing, -1 for creating
  customReportForPrint?: IReportView;
  showCheckPoints: boolean;
  splitOnTime: boolean;
  dashboardWidth: number | undefined;
  dashboardHeight: number | undefined;
  dashboardLinks: DashboardLink[];
  similarPastYearPeriodLength: number;
  timeScale: TimeScale;
  order?: number;
  timeLength: string;
  cumulativePercentageKey: string;
  totalTime: boolean;
  currentItems?: RequestData;
  recentItems?: RequestData;
  oldVersion?: boolean;
  isOpen: Set<string>;
  reportOverrides: {
    [key: number]: ReportOverride;
  };
  relatedSurveyId?: number;
  searchedReportIds: number[];
  reportItemMap: {
    [key: number]: { itemId: number; reportToken: string };
  };
  flag: string;
  viewOnlyInMainOwner: boolean;
};

const gaps = {
  day: '+1DAYS',
  week: '+7DAYS',
  month: '1',
  season: '3',
  twoSeason: '6',
  year: '12',
};

const jMonthItems = ['month', 'season', 'twoSeason', 'year'];

function areSetsEqual(a?: number[] | null, b?: number[] | null): boolean {
  if (!a && !b) return true;
  else if ((a && !b) || (b && !a)) return false;
  else if (a && b) {
    if (a.length !== b.length) return false;

    const setA = new Set(a);
    const setB = new Set(b);

    if (setA.size !== setB.size) return false;

    for (const val of setA) {
      if (!setB.has(val)) return false;
    }

    return true;
  }
  return true;
}
/* module to view all charts/dashboard/BSC in a survey/report*/
class ChartViewer extends React.Component<ComponentProps, ComponentState> {
  state: ComponentState = {
    collapsedCharts: [],
    editing: false,
    editFields: [],
    viewFields: [],
    loadingAnalyse: false,
    loading: true,
    reportName: '',
    reportTags: [],
    savingReport: false,
    selectedReport: null,
    editSeries: [],
    viewSeries: [],
    showCheckPoints: true,
    splitOnTime: false,
    dashboardWidth: undefined,
    dashboardHeight: undefined,
    dashboardLinks: [],
    similarPastYearPeriodLength: 0,
    timeScale: 'month',
    timeLength: '1',
    totalTime: false,
    display: 'List',
    cumulativePercentageKey: Math.random().toString(36).substring(7),
    isOpen: new Set<string>(),
    order: undefined,
    reportOverrides: {},
    searchedReportIds: [],
    reportItemMap: {},
    flag: '',
    viewOnlyInMainOwner: false,
  };

  private modalContent: React.RefObject<HTMLDivElement> = React.createRef();

  local: ILocal = (this.props?.surveyItem?.providerId
    ? this.props.providers.map?.[this.props.surveyItem.providerId]?.data
        ?.surveyOwnerProperties.localId || getCalendarLocal()
    : getCalendarLocal()) as ILocal;

  componentDidUpdate(prevProps: Readonly<ComponentProps>): void {
    if (prevProps.viewingVersion !== this.props.viewingVersion) {
      this.setState({
        collapsedCharts: [],
        editing: false,
        editFields: [],
        viewFields: [],
        loadingAnalyse: false,
        loading: true,
        reportName: '',
        reportTags: [],
        savingReport: false,
        selectedReport: null,
        editSeries: [],
        viewSeries: [],
        showCheckPoints: true,
        totalTime: false,
        display: this.props.viewType || 'List',
        mapRequest: undefined,
        mapResponse: undefined,
        cumulativePercentageKey: Math.random().toString(36).substring(7),
        flag: '',
      });
      this.componentDidMount();
    }
    if (
      this.props.reportItem &&
      ((this.props.viewType !== 'List' &&
        this.props.baseDate.getTime() !== prevProps.baseDate.getTime()) ||
        !areSetsEqual(
          this.props.externalTagFilter,
          prevProps.externalTagFilter
        ))
    ) {
      const reportList = this.getReports(
        this.props.reportItem,
        this.props.surveys
      );
      this.handleLoadAllReports(reportList as any, true);
    }
    if (
      this.props.surveyItem &&
      this.props.autoLoad &&
      (this.props.baseDate !== prevProps.baseDate ||
        !isEqual(
          this.props.extraFiltersIfNotSplit,
          prevProps.extraFiltersIfNotSplit
        ) ||
        this.props.surveys.data[this.props.surveyItem.surveyId].reports!
          .loaded !==
          prevProps.surveys.data[this.props.surveyItem.surveyId].reports!
            .loaded)
    ) {
      const reportList =
        this.props.surveys.data[this.props.surveyItem.surveyId].reports;
      if (reportList?.loaded) this.handleLoadAllReports(reportList.data, true);
    }
    if (this.props.viewType !== prevProps.viewType) {
      this.setState({
        display: this.props.viewType || 'List',
      });
    }
  }

  componentDidMount() {
    const { surveyItem, reportItem, survey, viewingVersion } = this.props;
    this.setState({
      display: this.props.viewType || 'List',
    });
    if (surveyItem) {
      const surveyToken =
        surveyItem.editToken ||
        surveyItem.changeDataToken ||
        surveyItem.reportToken ||
        surveyItem.fillToken;
      const finalViewingVersion = viewingVersion || survey!.currentVersion!;
      this.setState({
        oldVersion:
          (survey!.versions[finalViewingVersion].reportIds || []).length > 0,
      });
      const getSurveyReports =
        (survey!.versions[finalViewingVersion].reportIds || []).length > 0
          ? this.props.requireReportsByIds(
              survey!.versions[finalViewingVersion].reportIds!
            )
          : this.props.getReports(surveyToken);

      getSurveyReports
        .then(() => {
          const promises: Promise<any>[] = [];
          const surveyId = this.props.surveyId;
          promises.push(
            this.props.requireCheckPoints(surveyId, undefined, surveyToken)
          );
          promises.push(
            this.props.requireTags(
              this.props.dataContext.providerId ||
                this.props.reportItem?.item.data.providerId ||
                this.props.surveyItem?.providerId ||
                0
            )
          );
          return Promise.all(promises);
        })
        .then(() => {
          this.setState({ loading: false });
        })
        .catch((err: any) => {
          display(err);
        });
    } else if (reportItem) {
      this.fetchReportItems(reportItem);
    }
  }

  private fetchReportItems = async (reportItem: IOpenItem) => {
    const toFetchItemIds = new Set<number>();
    const searchedReportItemMap = {};
    let searchedReportList: {
      itemId: number;
      reportToken: string;
      report: IReportView;
    }[] = [];
    if (
      ((reportItem.item.data as IProviderSurveyReport).mandatorySearchTags
        ?.length ?? 0) !== 0
    ) {
      searchedReportList = (
        await (appDispatch(
          searchMainReports({
            mandatoryTagIds: (reportItem.item.data as IProviderSurveyReport)
              .mandatorySearchTags!,
            providerId: reportItem.item.data.providerId,
          })
        ) as ReturnType<typeof searchMainReportsFn>)
      ).reports;
      searchedReportList.forEach((it) => {
        searchedReportItemMap[it.report.id] = {
          itemId: it.itemId,
          reportToken: it.reportToken,
        };
      });
      this.setState({
        searchedReportIds: searchedReportList.map((it) => it.report.id),
      });
    }
    const reports: Array<{
      id: number;
      type: 'report' | 'survey';
      token?: string;
    }> = [];
    if ((reportItem.item.data as IProviderSurveyReport).reportIds) {
      (reportItem.item.data as IProviderSurveyReport).reportIds?.forEach(
        (id) => {
          reports.push({ id, type: 'report' });
        }
      );
    }
    if ((reportItem.item.data as IProviderSurveyReport).surveyReportLinks) {
      (
        reportItem.item.data as IProviderSurveyReport
      ).surveyReportLinks?.forEach((reportLink) => {
        reports.push({
          id: reportLink.reportId,
          type: 'survey',
          token: reportLink.surveyReportToken,
        });
        searchedReportItemMap[reportLink.reportId] = {
          itemId: reportLink.itemId,
          reportToken: reportLink.surveyReportToken,
        };

        if (!this.props.providerItems?.[reportLink.itemId!]) {
          toFetchItemIds.add(reportLink.itemId!);
        }
      });
    }

    this.setState({ reportItemMap: searchedReportItemMap });
    if (
      (reportItem.item.data as IProviderSurveyReport).taskReportList?.length ??
      0 > 0
    ) {
      if (!this.props.company?.templates)
        this.props.getCompany(this.props.managingCompanyId!);
    }

    this.props
      .requireReportsByIds(reports.map((item) => item.id))
      .then(() => {
        const ownerToken = (reportItem!.item.data as IProviderSurveyReport)
          .ownerToken;
        const promises: Promise<any>[] = [];
        const reportIds = new Set<number>();
        const surveyTokens = new Set<string>();

        reports.forEach((item) => {
          reportIds.add(item.id);
          if (item.type === 'survey') {
            surveyTokens.add(item.token as string);
          }
        });

        searchedReportList.forEach((it) => {
          reportIds.add(it.report.id);
          surveyTokens.add(it.reportToken);
          if (!this.props.providerItems[it.itemId!])
            toFetchItemIds.add(it.itemId);
        });

        if (toFetchItemIds.size)
          promises.push(this.props.getDocuments([...toFetchItemIds]));
        // if (
        //   (reportItem!.item.data as IProviderSurveyReport).reportToken ||
        //   ownerToken
        // )
        promises.push(
          this.props.requireAllCheckpoints(
            Array.from(reportIds),
            (reportItem!.item.data as IProviderSurveyReport).reportToken ||
              (reportItem!.item.data as IProviderSurveyReport).selfReportToken,
            ownerToken
          )
        );
        promises.push(this.props.requireAllSurveys(Array.from(surveyTokens)));
        promises.push(
          this.props.requireTags(
            this.props.dataContext.providerId ||
              this.props.reportItem?.item.data.providerId ||
              this.props.surveyItem?.providerId ||
              0
          )
        );
        return Promise.all(promises);
      })
      .then(() => {
        this.setState({ loading: false });
        const reportList = this.getReports(reportItem, this.props.surveys);
        if (this.props.viewType !== 'List')
          this.handleLoadAllReports(reportList as any);
      })
      .catch((err: any) => {
        display(err);
        this.setState({ loading: false });
      });
  };
  handleChangeTimeScale = (value: TimeScale) => {
    this.setState({ timeScale: value, timeLength: '1' });
  };

  handleChangeTimeLength = (value: string) => {
    this.setState({ timeLength: value });
  };

  handleChangeDisplay = (display: string) => {
    this.setState({ display: display as ReportViewType });
  };

  handleChangeFlag = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      this.setState({
        flag: 'MainReport',
      });
    } else {
      this.setState({
        flag: '',
      });
    }
  };

  handleChangeSettingInputs = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    if (name === 'reportName') {
      const value = e.target.value;
      this.setState({ reportName: value });
    }
    if (name === 'similarPastYearPeriodLength') {
      const value = e.target.value;
      this.setState({
        similarPastYearPeriodLength: Math.max(0, Number(value)),
      });
    }
    if (name === 'dashboardWidth') {
      const value = e.target.value;
      this.setState({ dashboardWidth: Math.max(0, Number(value)) });
    }
    if (name === 'dashboardHeight') {
      const value = e.target.value;
      this.setState({ dashboardHeight: Math.max(0, Number(value)) });
    }
    if (name === 'totalTime') {
      const value = e.target.checked;
      this.setState({ totalTime: value });
    }
    if (name === 'viewOnlyInMainOwner') {
      const value = e.target.checked;
      this.setState({ viewOnlyInMainOwner: value });
    } else if (name === 'splitOnTime') {
      const fieldsBucketCount = this.state.editFields.reduce(
        (splitCount, field) => {
          if (field.split) {
            splitCount += 1;
          }
          return splitCount;
        },
        0
      );
      const seriesBucketCount = this.state.editSeries.reduce(
        (bucketCount, seriesItem) => {
          if (
            (seriesItem.questionId && seriesItem.questionId === '0') ||
            (seriesItem.subQuestionId && seriesItem.subQuestionId === '')
          ) {
            bucketCount += 1;
          }
          if (seriesItem.splitOnFieldValues) bucketCount += 1;
          return bucketCount;
        },
        0
      );
      if (fieldsBucketCount + seriesBucketCount >= 2) {
        notify(
          i18n.t('couldNotSelectMoreThatTwoSplitItem', { ns: 'survey' }),
          'warning'
        );
      } else {
        const value = e.target.checked;
        this.setState({
          splitOnTime: value,
          totalTime: value ? this.state.totalTime : false,
        });
      }
    }
  };

  handleChangeReportTags = (reportTags: IOption[]) => {
    this.setState({ reportTags });
  };

  handleRemoveField = (fieldIndex: number) => {
    this.setState((prevState) => {
      const editFields = cloneDeep(prevState.editFields);
      editFields.splice(fieldIndex, 1);
      return { editFields };
    });
  };

  // mergeResponse = (
  //   mapRequest: MapRequest | undefined,
  //   mapResponse: MapResponse | undefined,
  //   reports: Map<number, IReportView>
  // ) => {
  //   if (!mapRequest || !mapResponse) {
  //     return undefined;
  //   }
  //   const ret: {[key:string]:IAnalyticResponse} = { };
  //   let minMonth = 0;
  //   let maxMonth = 0;
  //   .entries(mapRequest)
  //     .sort((a, b) => a[1].order - b[1].order)
  //     .forEach((item) => {
  //       const reportId = item[0];
  //       const analyse = mapResponse![reportId];
  //       const report: IReportView = reports.get(Number(reportId));
  //       if(analyse[0] && report?.get('name'))
  //         ret[report.get('name')] = analyse[0]
  //     });
  //   mapResponse;
  // };

  handleChangeField = (index: number, field: TReportField) => {
    if (index === -1) {
      // add new field
      this.setState((prevState) => {
        const editFields = cloneDeep(prevState.editFields);
        editFields.push(field);
        return { editFields };
      });
    } else {
      // update field
      this.setState((prevState) => {
        const editFields = cloneDeep(prevState.editFields);
        editFields[index] = field;
        return { editFields };
      });
    }
  };

  handleAddSeries = (seriesType: number | string) => {
    const { surveys, surveyId } = this.props;
    this.setState((prevState) => {
      const newSeries: ISeries = {
        metric: 'count',
        type: seriesType as SeriesType,
        customId: prevState.editSeries.length.toString(),
        chartType: null,
      };
      if (seriesType === 'question') {
        newSeries.questionId = null;
        newSeries.subQuestionId = null;
        if (!surveys.data[surveyId!]?.data) {
          newSeries.questionId = '0';
        }
      } else if (seriesType === 'tag') {
        newSeries.tagId = null;
        newSeries.tagIds = [];
      }
      const series = [...prevState.editSeries, newSeries];
      return { editSeries: series };
    });
  };

  handleChangeSeriesItem = (index: number, series: ISeries) => {
    this.setState((prevState) => {
      const seriesList = cloneDeep(prevState.editSeries);
      seriesList[index] = series;
      return { editSeries: seriesList };
    });
  };
  onMoveItem = (from: number, to: number) => {
    this.setState((prevState) => {
      const seriesList = cloneDeep(prevState.editSeries);
      const item = seriesList[from];
      seriesList.splice(from, 1);
      seriesList.splice(to, 0, item);
      return { editSeries: seriesList };
    });
  };
  handleLinksChanged = (dashboardLinks: DashboardLink[]) => {
    this.setState({ dashboardLinks });
  };

  handleRemoveSeriesItem = (index: number) => {
    this.setState((prevState) => {
      const seriesList = cloneDeep(prevState.editSeries);
      seriesList.splice(index, 1);
      return { editSeries: seriesList };
    });
  };

  maxSplitCount = () => {
    let ret = 0;
    this.state.editFields.forEach((e: IReportField) => {
      if (e.split) ret++;
    });
    if (this.state.splitOnTime || this.state.similarPastYearPeriodLength > 0)
      ret++;
    let seriesMax = 0;
    this.state.editSeries.forEach((e: ISeries) => {
      let current = 0;
      if (e.questionId === undefined) current = 0;
      else if (e.questionId === '0' || e.questionId?.toString().includes(',')) {
        current = 1;
      } else if (e.split) {
        current = 1;
      } else {
        const question =
          this.props.survey?.questionMap?.[Number(e.questionId) || 0]?.data;
        if (question && question.type === 'multipleAnswer') {
          if (e.subQuestionId && e.splitOnValues) current++;
          if (e.splitOnFieldValues) current++;
        } else {
          if (e.subQuestionId === '') {
            if (e.splitOnValues) {
              current = 2;
            } else {
              current = 1;
            }
          }
        }
      }
      seriesMax = Math.max(seriesMax, current);
    });
    return ret + seriesMax;
  };

  saveTaskReport = () => {
    const { reportItem } = this.props;
    const item = cloneDeep(reportItem!.item).data as IProviderSurveyReport;
    const taskReport = item.taskReportList?.find(
      (it) => it.id === this.state.selectedReport
    );
    if (taskReport) {
      taskReport.name = this.state.reportName;
      taskReport.dashboardWidth = this.state.dashboardWidth;
      taskReport.dashboardHeight = this.state.dashboardHeight;
      taskReport.tags = this.state.reportTags.map((it) => it.value.toString());
    }
    this.props.updateSurveyReportItem(item).catch((err) => {
      display(err);
    });
  };

  setOverrides = (reportId: number, values: { key: string; value: any }[]) => {
    const id = reportId;
    const newOverrides = cloneDeep(this.state.reportOverrides);
    if (!newOverrides[id]) {
      newOverrides[id] = {};
    }
    let reFetch = false;
    values.forEach((it) => {
      if (it.key !== 'chartType') reFetch = true;
      newOverrides[id][it.key] = it.value;
    });
    newOverrides[id].loading = reFetch;
    this.setState({ reportOverrides: newOverrides }, () => {
      if (!reFetch) return;
      // const hasNonCurrentReport = this.state.currentItems!.find(
      //   (it) => it.reportId !== reportId.toString()
      // );
      if (reportId && reportId !== -1) {
        let reports: ISurveyReports['data'] = {};
        if (this.props.surveyId) {
          reports = Object.values(this.props.surveys.reports.data).filter(
            (report: IReportView) => report.surveyId === this.props.surveyId
          );
        } else if (this.props.reportItem) {
          reports = this.getReports(this.props.reportItem, this.props.surveys);
        }
        const report = reports[id] || this.state.allReports?.[id];
        const items = this.generateRequest(report);
        this.fetchData(items!, report, true).then(() => {
          const newOverrides2 = cloneDeep(newOverrides);
          newOverrides2[id].loading = false;
          this.setState({ reportOverrides: newOverrides2 });
        });
      } else {
        this.generateRequestForNewReport(reportId);
      }
      //   if (reportId) {
      //     this.handleChooseAReport(
      //       Number(reportId),
      //       this.state.editing ? 'edit' : '',
      //       true
      //     );
      //   } else
      // }
    });
  };

  deleteTaskReport = (reportId: number) => {
    const { reportItem } = this.props;
    const item = cloneDeep(reportItem!.item).data as IProviderSurveyReport;
    item.taskReportList =
      item.taskReportList?.filter((it) => it.id !== reportId) ?? [];
    this.props.updateSurveyReportItem(item).catch((err) => {
      display(err);
    });
  };

  handleSaveReport = () => {
    if (
      (
        this.props.reportItem?.item.data as IProviderSurveyReport
      )?.taskReportList?.find((it) => it.id === this.state.selectedReport)
    ) {
      this.saveTaskReport();
      return;
    }
    const maxSplitCount = this.maxSplitCount();
    if (this.state.editSeries.length === 0) {
      notify(i18n.t('seriesCouldNotBeEmpty', { ns: 'survey' }), 'error');
    } else if (this.state.reportName.length === 0) {
      notify(i18n.t('reportNameCouldNotBeEmpty', { ns: 'survey' }), 'error');
    } else if (maxSplitCount > 2) {
      notify(i18n.t('moreThan2SplitsImpossible', { ns: 'survey' }), 'error');
    } else {
      this.setState({ savingReport: true });
      const time: ITimeInfo = {
        splitOnTime: this.state.splitOnTime,
        timeLength: Number(this.state.timeLength),
        timeScale: this.state.timeScale,
        totalTime: this.state.totalTime,
        similarPastYearPeriodLength: this.state.similarPastYearPeriodLength,
      };

      const tags: string[] = this.state.reportTags.map((t) => t.label);

      const report: IReportView = {
        fields: this.state.editFields,
        series: this.state.editSeries,
        name: this.state.reportName,
        time,
        surveyId: this.props.surveyId,
        id: this.state.selectedReport ? this.state.selectedReport : 0,
        tags,
        dashboardHeight: this.state.dashboardHeight,
        dashboardWidth: this.state.dashboardWidth,
        dashboardLinks: this.state.dashboardLinks,
        order:
          this.props.surveys.reports.data[this.state.selectedReport ?? 0]
            ?.order || Math.random() * 1000,
        flag: this.state.flag,
        viewOnlyInMainOwner: this.state.viewOnlyInMainOwner,
      };
      this.saveReport(report);
    }
  };

  handleGenerateReport = (
    questionId: number | undefined,
    options: { [key: string]: string }
  ) => {
    const { surveyItem } = this.props;
    if (surveyItem && surveyItem.editToken) {
      return this.props.generateReport(
        questionId,
        options,
        surveyItem.editToken
      );
    }
    return Promise.reject();
  };
  saveReport = (report: IReportView) => {
    let surveyToken;
    let ownerToken;
    let reportToken;
    const { surveyItem, reportItem } = this.props;
    const isUpdate = report.id > 0;
    if (!isUpdate && isANumber(this.state.order)) {
      report.order = this.state.order!;
    }
    if (surveyItem) {
      // save report for survey
      surveyToken =
        surveyItem.editToken ||
        surveyItem.changeDataToken ||
        surveyItem.reportToken ||
        surveyItem.fillToken;
      return this.props
        .saveReport(
          report,
          isUpdate,
          surveyToken,
          undefined,
          surveyItem.extraOwnerTokens?.[0]
        )
        .then(({ reportId }) => {
          if (!isUpdate) {
            this.setState({ selectedReport: reportId });
          }
          this.setState({ savingReport: false });
          notify(i18n.t('reportSavedSuccessfully', { ns: 'survey' }));
        })
        .catch((err) => {
          this.setState({ savingReport: false });

          display(err);
        });
    } else if (reportItem) {
      // save report for surveyReport item
      if ((reportItem.item.data as IProviderSurveyReport).ownerToken) {
        ownerToken = (reportItem.item.data as IProviderSurveyReport).ownerToken;
      } else {
        reportToken = (reportItem.item.data as IProviderSurveyReport)
          .reportToken;
      }
      return this.props
        .saveReport(report, isUpdate, undefined, ownerToken, reportToken)
        .then(({ reportId }) => {
          // save report id in surveyReport item
          const openItem = this.props.reportItem as IOpenItem;
          const item = cloneDeep(openItem.item).data as IProviderSurveyReport;
          if (item.reportIds) {
            item.reportIds.push(reportId);
          } else {
            item.reportIds = [reportId];
          }
          if (!isUpdate) {
            this.setState({ selectedReport: reportId });
            return this.props.updateSurveyReportItem(item);
          }
          // Not all code paths return a value
          return null;
        })
        .then(() => {
          this.setState({ savingReport: false });
          notify(i18n.t('reportSavedSuccessfully', { ns: 'survey' }));
        })
        .catch((err) => {
          this.setState({ savingReport: false });

          display(err);
        });
    }
    return Promise.reject();
  };

  handleChooseAReport = async (
    reportId: number | null,
    type: string,
    noLoading?: boolean
  ) => {
    const { surveys, reportItem } = this.props;
    const foundTaskReport = (
      reportItem?.item.data as IProviderSurveyReport
    )?.taskReportList?.find((it) => it.id === reportId);
    if (foundTaskReport) {
      this.setState({
        selectedReport: reportId,
        mapRequest: { [reportId!.toString()]: { data: {}, order: 1 } },
        mapResponse: { [reportId!.toString()]: {} },
        editing: type === 'edit',
        editType: 'task',
        dashboardHeight: foundTaskReport.dashboardHeight,
        dashboardWidth: foundTaskReport.dashboardWidth,
        reportName: foundTaskReport.name || '',
      });
      return;
    }
    if (reportId && reportId !== -1 /*new report*/) {
      const report: IReportView =
        surveys.reports.data[reportId] || this.state.allReports?.[reportId];

      const reportTags = report.tags.reduce((tmp: IOption[], tag: string) => {
        tmp.push({
          value: tag,
          label: tag,
        });
        return tmp;
      }, []);
      const {
        fields,
        series,
        time,
        dashboardWidth,
        dashboardHeight,
        dashboardLinks,
        flag,
      } = report;
      const seriesWithId = series.map((s, index) => {
        s = {
          ...s,
          customId: index.toString(),
        };
        return s;
      });
      const reportName = report.name;
      const reportState = {
        timeScale: time.timeScale,
        timeLength: time.timeLength.toString(),
        splitOnTime: time.splitOnTime,
        similarPastYearPeriodLength: time.similarPastYearPeriodLength || 0,
        dashboardWidth: dashboardWidth || 0,
        dashboardHeight: dashboardHeight || 0,
        dashboardLinks: dashboardLinks || [],
        totalTime: time.totalTime,
        editFields: fields,
        editSeries: seriesWithId,
        viewFields: fields,
        viewSeries: seriesWithId,
        reportName,
        selectedReport: reportId,
        loadingAnalyse: !noLoading,
        editing: type === 'edit',
        editType: '',
        loading: false,
        savingReport: false,
        reportTags,
        mapResponse: undefined,
        mapRequest: undefined,
        multipleRequest: false,
        relatedSurveyId: report.surveyId,
        viewOnlyInMainOwner: !!report.viewOnlyInMainOwner,
        allReports: { ...this.state.allReports, [report.id]: report },
        flag: flag ?? '',
      };
      this.setState(reportState);
      const items = this.generateRequest(report);
      if (items) {
        this.fetchData(items, report).then(() => {
          const newOverrides = cloneDeep(this.state.reportOverrides || {});
          if (newOverrides[reportId]) newOverrides[reportId].loading = false;
          this.setState({
            loadingAnalyse: false,
            reportOverrides: newOverrides,
          });
        });
      } else {
        this.setState({
          loadingAnalyse: false,
        });
      }
    } else {
      this.setState({
        timeScale: 'month',
        timeLength: '1',
        splitOnTime: false,
        similarPastYearPeriodLength: 0,
        editFields: [],
        editSeries: [],
        viewFields: [],
        viewSeries: [],
        editType: '',
        reportName: '',
        selectedReport: reportId,
        editing: type === 'edit',
        mapResponse: undefined,
        mapRequest: undefined,
        order: undefined,
        relatedSurveyId: undefined,
        flag: '',
      });
    }
  };

  handleAddOrUpdateTaskReport = (reportConf: any) => {
    const { reportItem } = this.props;
    const item = cloneDeep(reportItem!.item).data as IProviderSurveyReport;
    if (!reportConf.id) reportConf.id = randomNumber(18);
    if (!item.taskReportList) {
      item.taskReportList = [];
    }
    item.taskReportList = item.taskReportList.filter(
      (it) => it.id !== reportConf.id
    );
    item.taskReportList.push(reportConf);
    delete reportConf.templateName;
    delete reportConf.partName;
    this.props
      .updateSurveyReportItem(item)
      .then((item) => {
        const id = item.taskReportList![item.taskReportList.length - 1].id;
        setTimeout(() => {
          this.handleChooseAReport(id || null, 'edit');
        }, 100);
      })
      .catch((err) => {
        display(err);
      });
  };

  // handleSaveReportItem = (item: IProviderSurveyReport) => {
  //   const { reportItem } = this.props;
  //   const item = cloneDeep(reportItem!.item).data;
  //   this.props.updateSurveyReportItem(item).catch((err) => {
  //     display(err);
  //   });
  // };

  handleDeleteReportsInBulk = (reportIds: number[]) => {
    const { surveyItem, reportItem } = this.props;
    if (this.props.reportItem?.item) {
      const clone = cloneDeep(this.props.reportItem?.item)
        .data as IProviderSurveyReport;
      clone.taskReportList =
        clone.taskReportList?.filter((it) => !reportIds.includes(it.id!)) ?? [];
      if (surveyItem || clone.surveyReportLinks) {
        clone.surveyReportLinks =
          clone?.surveyReportLinks?.filter(
            (reportLink) => !reportIds.includes(reportLink.reportId)
          ) ?? [];
      }
      if (reportItem || clone.reportIds) {
        clone.reportIds =
          clone.reportIds?.filter((id) => !reportIds.includes(id)) ?? [];
      }
      this.props.updateSurveyReportItem(clone).catch((err) => {
        display(err);
      });
    } else {
      let hasErr = false;
      const surveyToken =
        surveyItem?.editToken ||
        surveyItem?.changeDataToken ||
        surveyItem?.reportToken ||
        surveyItem?.fillToken;
      for (const reportId of reportIds) {
        this.props.deleteReport(reportId, surveyToken).catch((err: any) => {
          hasErr = true;
          display(err);
        });
      }
      if (!hasErr) {
        this.setState({
          timeScale: 'month',
          timeLength: '1',
          splitOnTime: false,
          similarPastYearPeriodLength: 0,
          editFields: [],
          editSeries: [],
          reportName: '',
          selectedReport: null,
          editing: false,
        });
      }
    }
  };

  handleDeleteReport = (reportId: number) => {
    if (
      (
        this.props.reportItem?.item?.data as IProviderSurveyReport
      )?.taskReportList?.find((it) => it.id === reportId)
    ) {
      this.deleteTaskReport(reportId);
      return;
    }
    const { surveyItem, reportItem } = this.props;
    if (surveyItem) {
      const surveyToken =
        surveyItem.editToken ||
        surveyItem.changeDataToken ||
        surveyItem.reportToken ||
        surveyItem.fillToken;
      this.props
        .deleteReport(reportId, surveyToken)
        .then(() => {
          this.setState({
            timeScale: 'month',
            timeLength: '1',
            splitOnTime: false,
            similarPastYearPeriodLength: 0,
            editFields: [],
            editSeries: [],
            reportName: '',
            selectedReport: null,
            editing: false,
          });
        })
        .catch((err: any) => {
          display(err);
        });
    } else if (reportItem) {
      const item = cloneDeep(reportItem.item).data as IProviderSurveyReport;
      const report = this.props.surveys.reports.data[reportId];
      if (report.surveyId) {
        if (item.surveyReportLinks) {
          item.surveyReportLinks = item.surveyReportLinks.filter(
            (reportLink) => reportLink.reportId !== reportId
          );
        }
        this.props.updateSurveyReportItem(item).catch((err) => {
          display(err);
        });
      } else {
        if (item.reportIds) {
          item.reportIds = item.reportIds.filter((id) => id !== reportId);
          this.props.updateSurveyReportItem(item).catch((err) => {
            display(err);
          });
        }
      }
    }
  };

  hasProviderInstanceFilterOrBucket = (
    filters: IAnalyticsQuery['filters'],
    buckets: IAnalyticsQuery['buckets']
  ): boolean => {
    const bucketsHaveInstanceField = !!buckets.find(
      (bucket) =>
        bucket.field === 'instance' && bucket.customField === 'Provider'
    );
    const subBucketsHaveAInstanceField = !!buckets.find(
      (bucket) =>
        !!bucket.subBuckets &&
        !!bucket.subBuckets.find(
          (subBucket) =>
            subBucket.field === 'instance' && bucket.customField === 'Provider'
        )
    );
    const filtersHaveAInstanceField = !!filters.find(
      (filter) =>
        filter.field === 'instance' && filter.customField === 'Provider'
    );
    return (
      bucketsHaveInstanceField ||
      subBucketsHaveAInstanceField ||
      filtersHaveAInstanceField
    );
  };
  hasUserInstanceFilterOrBucket = (
    filters: IAnalyticsQuery['filters'],
    buckets: IAnalyticsQuery['buckets']
  ): boolean => {
    const bucketsHaveInstanceField = !!buckets.find(
      (bucket) => bucket.field === 'instance' && bucket.customField === 'User'
    );
    const subBucketsHaveAInstanceField = !!buckets.find(
      (bucket) =>
        !!bucket.subBuckets &&
        !!bucket.subBuckets.find(
          (subBucket) =>
            subBucket.field === 'instance' && subBucket.customField === 'User'
        )
    );
    const filtersHaveAInstanceField = !!filters.find(
      (filter) => filter.field === 'instance' && filter.customField === 'User'
    );
    return (
      bucketsHaveInstanceField ||
      subBucketsHaveAInstanceField ||
      filtersHaveAInstanceField
    );
  };

  extractProviderIdsFromBuckets = (buckets: IQueryBucket[]) => {
    return buckets.reduce(
      (
        providersId: number[],
        { field, validBuckets, subBuckets, customField }
      ) => {
        if (
          field === 'instance' &&
          validBuckets &&
          customField === 'Provider'
        ) {
          providersId.push(Number(validBuckets));
        }
        if (subBuckets) {
          providersId.push(...this.extractProviderIdsFromBuckets(subBuckets));
        }
        return providersId;
      },
      []
    );
  };
  extractUserIdsFromBuckets = (buckets: IQueryBucket[]) => {
    return buckets.reduce(
      (
        providersId: number[],
        { field, validBuckets, subBuckets, customField }
      ) => {
        if (field === 'instance' && validBuckets && customField === 'User') {
          providersId.push(Number(validBuckets));
        }
        if (subBuckets) {
          providersId.push(...this.extractUserIdsFromBuckets(subBuckets));
        }
        return providersId;
      },
      []
    );
  };

  makeProvidersIdList = (
    buckets: IAnalyticsQuery['buckets'],
    filters: IAnalyticsQuery['filters'],
    categories?: IAnalyticResponse['categories']
  ) => {
    const providersId: number[] = this.extractProviderIdsFromBuckets(buckets);
    filters.forEach(({ field, values, customField }) => {
      if (field === 'instance' && values && customField === 'Provider') {
        providersId.push(...(values as string[]).map(Number));
      }
    });
    if (categories && 'TermByInstanceProvider' in categories) {
      categories.TermByInstanceProvider.buckets.forEach(
        ({ val, categories }) => {
          providersId.push(val as number);
          if (categories) {
            const subIds = this.makeProvidersIdList(
              buckets,
              filters,
              categories
            );
            providersId.push(...subIds);
          }
        }
      );
    }
    return removeDuplicates(providersId);
  };

  makeUserIdList = (
    buckets: IAnalyticsQuery['buckets'],
    filters: IAnalyticsQuery['filters'],
    categories?: IAnalyticResponse['categories']
  ) => {
    const usersId: number[] = this.extractUserIdsFromBuckets(buckets);
    filters.forEach(({ field, values, customField }) => {
      if (field === 'instance' && values && customField === 'User') {
        usersId.push(...(values as string[]).map(Number));
      }
    });
    if (categories && 'TermByInstanceUser' in categories) {
      categories.TermByInstanceUser.buckets.forEach(({ val, categories }) => {
        usersId.push(val as number);
        if (categories) {
          const subIds = this.makeUserIdList(buckets, filters, categories);
          usersId.push(...subIds);
        }
      });
    }
    if (categories) {
      Object.values(categories)?.forEach((it) => {
        it?.buckets?.forEach((val) => {
          if (val?.categories?.['TermByInstanceUser']) {
            usersId.push(
              ...this.makeUserIdList(buckets, filters, val.categories)
            );
          }
        });
      });
    }
    return removeDuplicates(usersId).filter((it) => isANumber(it));
  };

  generateRequestForNewReport = (
    reportId?: number,
    reportObj?: IReportView
  ) => {
    if (reportObj) {
      this.setState({
        editFields: reportObj.fields,
        editSeries: reportObj.series,
        timeScale: reportObj.time.timeScale,
        timeLength: reportObj.time.timeLength.toString(),
        reportName: reportObj.name,
        splitOnTime: reportObj.time.splitOnTime,
        similarPastYearPeriodLength:
          reportObj.time.similarPastYearPeriodLength || 0,
        totalTime: reportObj.time.totalTime,
        selectedReport: reportObj.id,
        customReportForPrint: reportObj,
      });
    }
    const {
      editFields,
      editSeries,
      timeScale,
      timeLength,
      reportName,
      splitOnTime,
      similarPastYearPeriodLength,
      totalTime,
    } = this.state;
    const maxSplitCount = this.maxSplitCount();
    if (maxSplitCount > 2) {
      notify(i18n.t('moreThan2SplitsImpossible', { ns: 'survey' }), 'error');
      return;
    }
    const time: ITimeInfo = {
      splitOnTime,
      similarPastYearPeriodLength,
      timeLength: Number(timeLength),
      timeScale,
      totalTime:
        totalTime && Number(timeLength) > 1 && !similarPastYearPeriodLength,
    };
    const report: IReportView = reportObj || {
      id: reportId ? reportId : -1, // if reportId exists, it's editing mode. -1 means new report
      name: reportId ? reportName : 'New', // if reportId exists, it's editing mode. 'New' means new report
      time,
      series: editSeries,
      fields: editFields,
      tags: [],
      surveyId: this.props.surveyId,
      dashboardLinks: [],
    };
    this.setState({
      viewFields: report.fields,
      viewSeries: report.series,
      loadingAnalyse: true,
      mapResponse: undefined,
      mapRequest: undefined,
      multipleRequest: false,
      allReports: { ...this.state.allReports, [report.id]: report },
    });

    const items = this.generateRequest(report);
    if (items) {
      this.fetchData(items, report).then(() => {
        const newOverrides = cloneDeep(this.state.reportOverrides || {});
        if (newOverrides[reportId || -1])
          newOverrides[reportId || -1].loading = false;
        this.setState({
          reportOverrides: newOverrides,
          loadingAnalyse: false,
        });
      });
    } else {
      this.setState({
        loadingAnalyse: false,
      });
    }
  };

  generateRequest = (report: IReportView): null | RequestData => {
    const {
      series: seriesState,
      fields: fieldsState,
      time: timeState,
      id: reportId,
    } = report;
    const overrides = this.state.reportOverrides[report.id];
    const {
      splitOnTime: splitOnTimeState,
      timeScale: timeScaleState,
      timeLength: timeLengthState,
      similarPastYearPeriodLength: similarPastYearPeriodLengthState,
      totalTime,
    } = timeState || {};
    if (seriesState && seriesState.length === 0) {
      notify(i18n.t('seriesCouldNotBeEmpty', { ns: 'survey' }), 'error');
      return null;
    } else {
      const foundOverrides = [] as number[];
      return seriesState
        ?.map((series, index) => {
          if (this.props.viewType === 'BSC' && index > 0) return null;
          const customReport: CustomReport = {
            reportId,
            series,
            fields: [
              ...(fieldsState?.map((it) => {
                if (
                  !overrides ||
                  it.type !== 'question' ||
                  overrides?.[(it as IQuestionReportField).questionId] ===
                    undefined
                )
                  return it;
                else {
                  foundOverrides.push((it as IQuestionReportField).questionId);
                  const ret = cloneDeep(it);
                  (ret as IQuestionReportField).choiceIds = overrides[
                    (it as IQuestionReportField).questionId
                  ].map((it) => it.toString());
                  return ret;
                }
              }) || []),
              ...Object.entries(overrides || {}).reduce(
                (current, [key, value]) => {
                  if (Number(key) > 0 && !foundOverrides.includes(Number(key)))
                    current.push({
                      choiceIds: value,
                      includeTotal: false,
                      name: '',
                      questionId: Number(key),
                      split: false,
                      type: 'question',
                    } as IQuestionReportField);
                  return current;
                },
                [] as IQuestionReportField[]
              ),
            ],
            splitOnTime:
              (splitOnTimeState &&
                (Number(overrides?.timeLength || timeLengthState) || 1) > 1) ||
              (similarPastYearPeriodLengthState || 0) > 0,
            timeScale: overrides?.timeScale || timeScaleState,
            totalTime:
              totalTime &&
              (overrides?.timeLength || timeLengthState) > 1 &&
              !similarPastYearPeriodLengthState,
            similarPastYearPeriodLength: similarPastYearPeriodLengthState,
            timeLength: (overrides?.timeLength || timeLengthState).toString(),
            surveyId: report.surveyId,
            originalTimeScale: timeScaleState,
          };

          const requestQuery = this.makeAnalyticsQuery(customReport);
          if (!requestQuery) return null;
          let surveyToken: string | undefined;
          let ownerToken: string | undefined;
          let reportToken: string | undefined;
          const { surveyItem, reportItem } = this.props;
          if (surveyItem) {
            surveyToken =
              surveyItem.editToken ||
              surveyItem.changeDataToken ||
              surveyItem.reportToken ||
              surveyItem.fillToken;
            reportToken = surveyItem.extraOwnerTokens?.[0];
          } else if (reportItem) {
            if (report.surveyId) {
              surveyToken = this.state.reportItemMap[report.id]?.reportToken;
            } else {
              if ((reportItem.item.data as IProviderSurveyReport).ownerToken) {
                ownerToken = (reportItem.item.data as IProviderSurveyReport)
                  .ownerToken;
              } else {
                reportToken =
                  reportItem.item.data.reportToken ||
                  (reportItem.item.data as IProviderSurveyReport)
                    .selfReportToken;
              }
            }
          }

          if (series.metric === 'responseList') {
            return {
              reportId: reportId.toString(),
              index,
              requestQuery: {
                ...requestQuery,
                type: 'responseList',
                questionIdsFilter:
                  series.questionId
                    ?.split(',')
                    .filter((el) => !responseListQuestionFields.includes(el))
                    .map((el) => {
                      if (el.includes('@score')) {
                        return el.split('@score').at(0) as string;
                      } else return el;
                    }) ?? [],
              },
              surveyToken: surveyToken || report.surveyToken,
              ownerToken,
              reportToken,
              type: 'responseList',
            };
          }
          return {
            reportId: reportId.toString(),
            index,
            requestQuery,
            surveyToken: surveyToken || report.surveyToken,
            ownerToken,
            reportToken,
          };
        })
        .filter((e) => e !== null) as any;
    }
  };

  getFinalTimeLength = (
    timeLength: number,
    timeScale: TimeScale,
    similarPastYearPeriodLength: number,
    splitOnTime: boolean,
    totalTime: boolean
  ) => {
    if (!splitOnTime || totalTime)
      // similarPastYearPeriodLength not applied here
      return timeLength;
    switch (timeScale) {
      case 'week':
      case 'day':
      case 'year': // not applied here
        return timeLength;
      case 'month':
        return Math.max(timeLength, 1 + 12 * similarPastYearPeriodLength);
      case 'season':
        return Math.max(timeLength, 1 + 4 * similarPastYearPeriodLength);
      case 'twoSeason':
        return Math.max(timeLength, 1 + 2 * similarPastYearPeriodLength);
    }
  };
  makeAnalyticsQuery = (
    reportState: CustomReport
  ): IAnalyticsQuery | undefined => {
    const {
      series: seriesState,
      fields: fieldsState,
      splitOnTime: splitOnTimeState,
      similarPastYearPeriodLength: similarPastYearPeriodLengthState,
      timeScale: timeScaleState,
      timeLength: timeLengthState,
      totalTime,
      surveyId,
      originalTimeScale,
      reportId,
    } = reportState;
    const { thisUserId } = this.props;

    const finalTimeLength = this.getFinalTimeLength(
      Number(timeLengthState),
      timeScaleState,
      similarPastYearPeriodLengthState || 0,
      splitOnTimeState,
      totalTime
    );
    if (similarPastYearPeriodLengthState === 4) return {} as any;
    const metric: IQueryMetric = {
      metric:
        seriesState.metric === 'percentageOfCount'
          ? 'count'
          : seriesState.metric,
      field: /*seriesState.metric === 'sum' ? 'scoreSum' :*/ 'score',
      name: addScoreToMetric(seriesState.metric),
    };
    const metrics: IQueryMetric[] = [metric];

    const buckets: IQueryBucket[] = [];

    const filteredFields: Array<IReportField> = fieldsState.filter(
      (field: any) => {
        return field.split;
      }
    );
    const filteredSeries: any[] = [];
    if (
      seriesState.subQuestionId === '' ||
      seriesState.questionId === '0' ||
      seriesState.questionId?.toString().includes(',') ||
      seriesState.splitOnValues ||
      seriesState.split ||
      seriesState.splitOnFieldValues
    ) {
      filteredSeries.push(seriesState);
    }
    let filtersFromPermissionScheme: IQuestionReportField[] = [];
    const filtersNotes: string[] = [];
    const hasSupperReportAccess =
      !!(
        this.props.surveyItem &&
        (this.props.surveyItem.editToken ||
          this.props.surveyItem.changeDataToken)
      ) ||
      !!(
        this.props.reportItem &&
        (this.props.reportItem.item.data as IProviderSurveyReport)
          .changeDataToken
      );
    if (surveyId && !hasSupperReportAccess) {
      if (!this.props.surveyItem) {
        const relatedProviderSurveyItemId =
          this.state.reportItemMap[reportId ?? 0]?.itemId;
        if (
          relatedProviderSurveyItemId &&
          !this.props.providerItems?.[relatedProviderSurveyItemId]
        )
          return undefined;
      }
      const survey = this.props.surveys.data[surveyId];
      if (!survey?.data) return undefined;
      const surveyVersion =
        this.props.viewingVersion || survey.data?.currentVersion;
      const company = this.props.company;

      const companyMemberRoles = Object.keys(
        company?.members?.[thisUserId || 0]?.roles || {}
      );
      const relatedPermissions = [
        `U${thisUserId}`,
        ...(companyMemberRoles?.map((r) => `R${r}`) || []),
      ];
      let activeRelatedPermissions = this.props.surveyItem?.changeDataToken
        ? []
        : this.props.surveyItem?.activePermissions?.effectiveReportPermission?.filter(
            (it) => relatedPermissions.includes(it)
          ) || [];
      const filterQuestionsValues: { [key: number]: number[] } = {};
      const hiddenQuestionIds: number[] = [];
      filtersFromPermissionScheme = Object.values(
        survey.data.versions[surveyVersion].questionMap!
      )
        .sort((a, b) => a.order - b.order)
        .reduce((current: IQuestionReportField[], q) => {
          const condition = q.viewCondition;
          const id = q.id;
          if (condition) {
            const refAnswer = filterQuestionsValues[condition.refQuestionId];
            const ret =
              hiddenQuestionIds.includes(+condition.refQuestionId) ||
              (refAnswer &&
                !conditionMatchesAnswer(
                  {
                    type: 'multipleChoice',
                    selected: refAnswer,
                    questionId: condition.refQuestionId,
                    values: [],
                    value: '',
                  },
                  condition
                ));
            if (ret) {
              hiddenQuestionIds.push(+id);
              return current;
            }
          }
          const question = q as IMultipleChoiceQuestionView;
          const permissionScheme: number[] | undefined =
            question.reportPermissionSchemeList;
          if (permissionScheme && permissionScheme.length) {
            let choices: number[] = [];
            let validChoices: number[] | undefined;
            if (company && permissionScheme.length > 0) {
              validChoices = [];
              permissionScheme.forEach((ps) => {
                validChoices = [
                  ...(validChoices || []),
                  ...Object.entries(
                    company?.permissionSchemes?.[ps]?.properties
                      ?.roleIdTagIdListMap ?? {}
                  ).reduce((current: number[], [key, value]) => {
                    if (
                      this.props.currentUserCompanyRoles &&
                      this.props.currentUserCompanyRoles.includes(Number(key))
                    ) {
                      activeRelatedPermissions =
                        activeRelatedPermissions.filter(
                          (it) =>
                            it !== `R${key}` &&
                            !(
                              company.roles[Number(it.substr(1))]
                                ?.autoAddPrefix &&
                              isPrefix(
                                company.roles[Number(it.substr(1))]?.name,
                                company.roles[key].name
                              )
                            )
                        ); // remove role related to permission scheme
                      current = [...current, ...(value as any)];
                    }
                    return current;
                  }, [] as number[]),
                ];
                activeRelatedPermissions = activeRelatedPermissions.filter(
                  // remove compounding role from permission scheme
                  (it) => it !== `R${ps - AutoRolePermissionSchemeIndex}`
                ); // remove role related to permission scheme
              });
              if (activeRelatedPermissions.length) {
                // there exist a role not related to permission scheme so all items will be available
                validChoices = undefined;
              }
            } else {
              validChoices = undefined;
            }

            if (question.parentTagChoices) {
              question.parentTagChoices.forEach((parentId: number) => {
                Object.values(this.props.tags!.data!)
                  ?.filter(
                    (tag) =>
                      tag.parentId &&
                      tag.parentId === parentId &&
                      !tag.deleted &&
                      !question.excludedTagChoices?.includes(Number(tag.id)) &&
                      (validChoices?.includes(Number(tag.id)) ?? true)
                  )
                  .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
                  .map((item) => choices.push(Number(item.id)));
              });
            }
            if (question.tagChoices) {
              question.tagChoices?.forEach((tagChoice: number) => {
                if (!validChoices || validChoices.includes(tagChoice))
                  choices.push(tagChoice);
              });
            }
            choices = removeDuplicates(choices);
            if (choices.length !== 0 && validChoices) {
              const qrf: IQuestionReportField = {
                includeTotal: false,
                split: false,
                type: 'question',
                questionId: question.id,
                choiceIds: choices.map((it: number) => it.toString()),
                subQuestionId: null,
              };
              current.push(qrf);
              filtersNotes.push(
                question.title +
                  ': ' +
                  choices.reduce((current, item) => {
                    const name = this.props.tags?.data[item].text ?? '';
                    if (current.length > 0) return current + ', ' + name;
                    return name;
                  }, '')
              );
            }
            if (choices.length === 0) {
              const qrf: IQuestionReportField = {
                includeTotal: false,
                split: false,
                type: 'question',
                questionId: question.id,
                choiceIds: ['XX-XX'],
                subQuestionId: null,
              };
              current.push(qrf);
              filtersNotes.push(
                i18n.t('reportNotAccessible', { ns: 'survey' })
              );
            }
            filterQuestionsValues[question.id] = choices;
          }
          return current;
        }, [] as IQuestionReportField[]);
    }
    const endDate = endOfDate(this.props.baseDate, timeScaleState, true);
    let subtractedDate =
      this.local === 'fa_IR@calendar=persian'
        ? moment(endDate)
        : moment(endDate);
    if (timeScaleState === 'month' || timeScaleState === 'year') {
      const scale =
        this.local === 'fa_IR@calendar=persian'
          ? `j${upperFirst(timeScaleState)}`
          : `${upperFirst(timeScaleState)}`;
      subtractedDate = subtractedDate.subtract(
        finalTimeLength - 1,
        scale as moment.unitOfTime.DurationConstructor
      );
    } else if (timeScaleState === 'season') {
      subtractedDate = subtractedDate.subtract(
        (finalTimeLength - 1) * 3,
        (this.local === 'fa_IR@calendar=persian'
          ? 'jMonth'
          : 'month') as moment.unitOfTime.DurationConstructor
      );
    } else if (timeScaleState === 'twoSeason') {
      subtractedDate = subtractedDate.subtract(
        (finalTimeLength - 1) * 6,
        (this.local === 'fa_IR@calendar=persian'
          ? 'jMonth'
          : 'month') as moment.unitOfTime.DurationConstructor
      );
    } else {
      // day and week have not jalaali type
      subtractedDate = subtractedDate.subtract(
        finalTimeLength - 1,
        timeScaleState as moment.unitOfTime.DurationConstructor
      );
    }
    const startDate = startOfDate(
      subtractedDate.toDate(),
      timeScaleState,
      true,
      getLangBaseOnLocal(this.local)
    );

    const yearFormat =
      this.local === 'fa_IR@calendar=persian' ? 'jYYYY' : 'YYYY';
    const mFormat = this.local === 'fa_IR@calendar=persian' ? 'jM' : 'M';

    const startMonthIndex = jMonthIndex(
      Number(englishNumber(moment(startDate).format(yearFormat))),
      Number(englishNumber(moment(startDate).format(mFormat)))
    );
    const endMonthIndex =
      jMonthIndex(
        Number(englishNumber(moment(endDate).format(yearFormat))),
        Number(englishNumber(moment(endDate).format(mFormat)))
      ) + 1;

    const filteredList = [...filteredFields, ...filteredSeries];
    let bucketItem: IQueryBucket;
    if (splitOnTimeState) {
      const gap = gaps[timeScaleState];
      const subBucket = makeBucket(
        filteredList,
        surveyId ? this.props.surveys.data[surveyId]?.data : undefined,
        {
          tags: this.props.tags,
        }
      ) as IQueryBucket;
      const isJMonth = jMonthItems.includes(timeScaleState);
      bucketItem = {
        name: 'TermByTime',
        type: isJMonth ? 'rangeBucket' : 'dateRangeBucket',
        field: isJMonth ? 'effectiveJMonth' : 'effectiveDate',
        start: isJMonth ? startMonthIndex : null,
        end: isJMonth ? endMonthIndex : null,
        includeTotal: !!totalTime,
        gap,
        customField:
          Number(timeLengthState) === finalTimeLength
            ? {
                originalTimeScale: originalTimeScale,
                timeScale: timeScaleState,
              }
            : {
                originalTimeScale: originalTimeScale,
                timeScale: timeScaleState,
                timeLength: timeLengthState,
                similarPastYearPeriodLength:
                  similarPastYearPeriodLengthState || 0,
              },
        subBuckets: subBucket ? [subBucket] : [],
      };
      buckets.push(bucketItem);
    } else if (filteredList.length > 0) {
      bucketItem = makeBucket(
        filteredList,
        surveyId ? this.props.surveys.data[surveyId].data : undefined,
        {
          tags: this.props.tags,
        }
      ) as IQueryBucket;
      buckets.push(bucketItem);
    }

    const seriesFilters: IQueryFilter[] = makeFiltersFromSeries(
      seriesState,
      this.props.survey ||
        (surveyId ? this.props.surveys.data[surveyId].data : undefined)
    );

    const fieldsFilters: IQueryFilter[] = this.props.tags
      ? makeFiltersFromFields(fieldsState, this.props.tags)
      : [];

    const permissionSchemesFilters: IQueryFilter[] = this.props.tags
      ? makeFiltersFromFields(filtersFromPermissionScheme, this.props.tags)
      : [];

    const externalTagFilter =
      this.props.externalTagFilter && this.props.tags
        ? makeFiltersFromFields(
            this.props.externalTagFilter.map(
              (it) =>
                ({
                  type: 'tag',
                  values: [
                    {
                      tag: it,
                      child: false,
                    },
                  ],
                  includeTotal: false,
                  split: false,
                }) as ITagReportField
            ),
            this.props.tags
          )
        : [];
    const timeFilter: IQueryFilter = {
      type: 'dateRangeFilter',
      field: 'effectiveDate',
      start: startDate,
      end: endDate,
    };
    const survey = this.props.surveys.data[surveyId!];
    const surveyVersion =
      this.props.viewingVersion || (survey && survey.data?.currentVersion);

    const appliedExtraFilters = this.props.extraFiltersIfNotSplit?.filter(
      (filter) => {
        const [questionId, choice] = filter.values?.[0]
          ?.toString()
          .split('$$')
          ?.map((it) => Number(it)) || [undefined, undefined];
        if (questionId && choice) {
          const question =
            survey.data.versions[surveyVersion]!.questionMap![questionId];
          const parentTag = this.props.tags?.data?.[choice]?.parentId;
          if (
            !(question as IMultipleChoiceQuestionView)?.tagChoices?.includes(
              choice
            ) &&
            !(
              parentTag &&
              (
                question as IMultipleChoiceQuestionView
              )?.parentTagChoices?.includes(parentTag)
            )
          )
            return false;
        }
        return !buckets.find((bucket) => {
          if (this.filterIsPartOfBucket(bucket, filter)) return true;
          if (
            bucket.subBuckets?.find((subBucket) =>
              this.filterIsPartOfBucket(subBucket, filter)
            )
          ) {
            return true;
          }
          return false;
        });
      }
    );
    const extraFilterTitle: string[] = [];
    appliedExtraFilters?.forEach((filter) => {
      if (filter.field === 'allAnswers') {
        const survey = this.props.surveys.data[surveyId!];
        const surveyVersion =
          this.props.viewingVersion || survey.data.currentVersion;
        const [questionId, choice] = filter.values?.[0]
          ?.toString()
          .split('$$')
          ?.map((it) => Number(it)) || [undefined, undefined];
        if (questionId && choice) {
          const question =
            survey.data.versions[surveyVersion]!.questionMap![questionId];
          const choices = [choice];
          extraFilterTitle.push(
            question.title +
              ': ' +
              choices.reduce((current, item) => {
                const name = this.props.tags?.data[item].text ?? '';
                if (current.length > 0) return current + ', ' + name;
                return name;
              }, '')
          );
        }
      }
    });
    const filters = [
      ...seriesFilters,
      ...fieldsFilters,
      timeFilter,
      ...permissionSchemesFilters,
      ...externalTagFilter,
      ...(appliedExtraFilters || []),
    ];

    return {
      filters,
      buckets,
      metrics,
      filtersNotes,
      extraFilterTitle,
    };
  };

  private filterIsPartOfBucket(bucket: IQueryBucket, filter: IQueryFilter) {
    return !!(
      bucket.field === filter.field &&
      bucket.prefix &&
      filter.values?.[0].toString().startsWith(bucket.prefix)
    );
  }

  handleChangeBaseDate = (baseDate: Date) => {
    this.props.onChangeBaseDate(baseDate);
  };

  handleChangeShowCheckPoints = () => {
    this.setState((prevState) => ({
      showCheckPoints: !prevState.showCheckPoints,
    }));
  };

  handlePrint = (reportId: string) => () => {
    if (this.state.mapRequest && this.state.mapResponse) {
      const requestQuery = this.state.mapRequest[reportId];
      const analyse = this.state.mapResponse[reportId];
      let providers: number[] = [];
      Object.entries(requestQuery.data).forEach((item) => {
        const seriesIndex = item[0];
        const queryState = item[1];
        const analyseItem = analyse[seriesIndex];
        if (
          queryState.buckets &&
          this.hasProviderInstanceFilterOrBucket(
            queryState.filters,
            queryState.buckets
          )
        ) {
          const providersId = this.makeProvidersIdList(
            queryState.buckets,
            queryState.filters,
            analyseItem ? analyseItem.categories : undefined
          );
          providers = [...providers, ...providersId];
        }
      });
      let users: number[] = [];
      Object.entries(requestQuery.data).forEach((item) => {
        const seriesIndex = item[0];
        const queryState = item[1];
        const analyseItem = analyse[seriesIndex];
        if (
          queryState.buckets &&
          this.hasUserInstanceFilterOrBucket(
            queryState.filters,
            queryState.buckets
          )
        ) {
          const usersId = this.makeUserIdList(
            queryState.buckets,
            queryState.filters,
            analyseItem ? analyseItem.categories : undefined
          );
          users = [...users, ...usersId];
        }
      });

      const report: IReportView =
        this.props.surveys.reports.data[Number(reportId)] ??
        this.state.allReports?.[Number(reportId)] ??
        this.state.customReportForPrint;
      const reportObject = {
        analyse,
        collapsedCharts: this.state.collapsedCharts,
        fields: cloneDeep(report.fields),
        providerId:
          this.props.dataContext.providerId ||
          this.props.reportItem?.item.data?.providerId ||
          this.props.surveyItem?.providerId,
        providers: removeDuplicates(providers),
        users: removeDuplicates(users),
        report: cloneDeep(report),
        reportItem: this.props.reportItem
          ? this.props.reportItem.item.data
          : undefined,
        timeScale:
          this.state.reportOverrides?.[report.id]?.timeScale ||
          report.time.timeScale,
        requestQuery: requestQuery.data,
        showCheckPoints: this.state.showCheckPoints,
        series: cloneDeep(report.series),
        surveyItem:
          this.props.surveyItem || // when item is linked in dashboard
          this.props.providerItems[
            (
              this.props.reportItem?.item?.data as IProviderSurveyReport
            )?.surveyReportLinks?.find((it) => it.reportId === Number(reportId))
              ?.itemId || 0
          ]?.data,
        extraContext: this.props.extraContext,
      };
      const chartTypeOverride =
        this.state.reportOverrides[Number(reportId)]?.chartType;
      const chartColorOverride =
        this.state.reportOverrides[Number(reportId)]?.chartColor;
      const reportOverride = cloneDeep(
        this.state.reportOverrides[Number(reportId)] || {}
      );
      if (chartTypeOverride || chartColorOverride) {
        reportObject.series.forEach((it) => {
          if (chartTypeOverride) it.chartType = chartTypeOverride;
          if (chartColorOverride) it.color = chartColorOverride;
        });
      }
      localStorage.setItem(
        'printableReport',
        JSON.stringify({ ...reportObject, reportOverride })
      );
      const win = window.open('/print/survey', '_blank');
      if (win) {
        win.focus();
      }
    }
  };

  handleExport = (reportId: string) => (includeScore: boolean) => {
    if (
      this.state.mapRequest &&
      this.state.mapResponse &&
      this.state.currentItems
    ) {
      const query = this.state.mapRequest[reportId];
      const item = this.state.recentItems?.find(
        (it) => it.reportId === reportId
      );
      if (!item) return Promise.reject();
      const exportOptions: IExportResponsesQuery = {
        filters: query.data[0].filters,
        includeScore: includeScore,
        includeEmptyResponse: false,
      };
      return this.props.exportResponses(
        exportOptions,
        item.surveyToken,
        item.ownerToken,
        item.reportToken
      );
    }
    return Promise.reject();
  };

  handleCopyAReport = (reportId: number) => {
    let reports: ISurveyReports['data'] = {};
    if (this.props.surveyId) {
      reports = Object.values(this.props.surveys.reports.data).filter(
        (report: IReportView) => report.surveyId === this.props.surveyId
      );
    } else if (this.props.reportItem) {
      reports = this.getReports(this.props.reportItem, this.props.surveys);
    }
    const orderedIds = (
      (Object.values(reports).reduce(
        (current, report: any) => {
          current!.push({ id: report.id, order: report.order });
          return current!;
        },
        [] as Array<{ id: number; order: number }>
      ) || []) as Array<{ id: number; order: number }>
    ).sort((a, b) => a.order - b.order);
    const index = orderedIds.findIndex((x) => x.id === reportId);
    let order;
    if (index < 0) order = 0;
    else if (index < orderedIds.length - 1)
      order = (orderedIds[index].order + orderedIds[index + 1].order) / 2;
    else order = orderedIds[index].order + 1;
    const report = this.props.surveys.reports.data[reportId];
    this.setState({
      editing: true,
      editFields: report.fields,
      reportName: report.name,
      selectedReport: -1,
      editSeries: report.series,
      splitOnTime: report.time.splitOnTime,
      similarPastYearPeriodLength: report.time.similarPastYearPeriodLength!,
      timeLength: report.time.timeLength.toString(),
      timeScale: report.time.timeScale,
      order: order,
    });
  };

  fetchData = (
    items: RequestData,
    report: IReportView,
    replaceSameReportOnly?: boolean,
    allItems?: RequestData
  ): Promise<any[]> => {
    if (this._isUnmounted) {
      return Promise.resolve([]);
    }
    const promiseList: Promise<void>[] = [];
    let finalItems = items;
    if (replaceSameReportOnly) {
      const index =
        this.state.currentItems?.findIndex((it) => {
          it.reportId === items[0].reportId;
        }) ||
        this.state.currentItems?.length ||
        0;
      finalItems =
        this.state.currentItems?.splice(index, items.length, ...items) || items;
    }
    this.setState({
      currentItems: finalItems,
      recentItems: [
        ...(allItems || finalItems),
        ...(this.state.recentItems || []),
      ],
    });
    items.forEach((item) => {
      if (item.type === 'responseList') {
        promiseList.push(
          appDispatch(
            getAnalyseList({
              dataQuery: Object.keys(item.requestQuery || {})
                .filter((el: string) => el !== 'metrics')
                .reduce((acc: any, el: string) => {
                  acc[el] = (item?.requestQuery?.[el] as any) || {};
                  return acc;
                }, {}) as IAnalyticsQuery,
              surveyToken: item.surveyToken,
              ownerToken: item.ownerToken,
              reportToken: item.reportToken,
              from: 0,
              count: 1000,
              questionIdsFilter: item.requestQuery.questionIdsFilter
                ?.filter((el) => !responseListQuestionFields.includes(el))
                .map((el) => {
                  if (el.includes('@score')) {
                    return el.split('@score').at(0) as string;
                  } else return el;
                }),
            })
          ).then((res: any) => {
            const analyse = res.result.response;
            this.finalSetRequestAndResponse(report, item, analyse);
          })
        );
      } else if (item.type === 'task') {
        const reportInfo = (
          this.props.reportItem?.item.data as IProviderSurveyReport
        ).taskReportList?.find((it) => it.id === Number(item.reportId));
        if (reportInfo) {
          const templateReport = getTemplateReportMark(
            this.props.tasks[reportInfo.templateId].templateReportOptions!
          );
          this.props.getTaskReport(
            reportInfo.companyId,
            templateReport,
            0,
            0,
            reportInfo.projectId as any,
            reportInfo.parentId as any,
            reportInfo.templateId,
            reportInfo.assigneeId as any,
            reportInfo.splitMonths,
            reportInfo.splitField,
            reportInfo.fieldMap
          );
        }
      } else
        promiseList.push(
          this.props
            .getAnalyse(
              item.requestQuery,
              item.surveyToken,
              item.ownerToken,
              item.reportToken
            )
            .then(async ({ result: analyse }) => {
              if (
                item.requestQuery.buckets &&
                this.hasProviderInstanceFilterOrBucket(
                  item.requestQuery.filters,
                  item.requestQuery.buckets
                )
              ) {
                const providersId = this.makeProvidersIdList(
                  item.requestQuery.buckets,
                  item.requestQuery.filters,
                  analyse.categories
                );
                await this.props.getAllProviders(providersId);
              }
              if (
                item.requestQuery.buckets &&
                this.hasUserInstanceFilterOrBucket(
                  item.requestQuery.filters,
                  item.requestQuery.buckets
                )
              ) {
                const userIds = this.makeUserIdList(
                  item.requestQuery.buckets,
                  item.requestQuery.filters,
                  analyse.categories
                );
                await this.props.requireSeveralUser(userIds);
              }

              this.finalSetRequestAndResponse(report, item, analyse);
              if (this.modalContent?.current && !replaceSameReportOnly) {
                this.modalContent.current.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                });
              }
            })
        );
    });
    return Promise.allSettled(promiseList);
  };

  private finalSetRequestAndResponse(
    report: IReportView,
    item: {
      index: number;
      reportId: string;
      ownerToken?: string;
      surveyToken?: string;
      reportToken?: string;
      requestQuery: IAnalyticsQuery;
    },
    analyse: IAnalyticResponse
  ) {
    this.setState((prevState) => {
      const analyseMap: IAnalyseState = prevState.mapResponse
        ? {
            ...prevState.mapResponse[report.id],
          }
        : {};
      analyseMap[item.index] = analyse;
      const newMapResponse = {
        ...prevState.mapResponse,
        [report.id]: analyseMap,
      };
      const requestMap: IQueryState =
        prevState.mapRequest && prevState.mapRequest[report.id]
          ? {
              ...prevState.mapRequest[report.id].data,
            }
          : {};
      requestMap[item.index] = item.requestQuery;
      const newMapRequest = {
        ...prevState.mapRequest,
        [report.id]: {
          data: requestMap,
          order: report.order,
        },
      };

      return {
        mapResponse: newMapResponse,
        mapRequest: newMapRequest,
      };
    });
  }

  handleLoadAllReports = (
    reports: ISurveyReportMap,
    keepCollapsState: boolean = false
  ) => {
    this.setState({
      multipleRequest: true,
      loadingAnalyse: true,
      mapResponse: undefined,
      mapRequest: undefined,
      selectedReport: null,
      collapsedCharts: keepCollapsState
        ? this.state.collapsedCharts
        : Object.keys(reports || {}).map((it) => Number(it)),
      cumulativePercentageKey: Math.random().toString(36).substring(7),
      allReports: reports,
    });
    const requestList: Promise<any>[] = [];
    const allItems: RequestData = [];
    Object.values(reports || {}).forEach((report) => {
      const items = this.generateRequest(report);
      if (items) {
        items.forEach((it) => allItems.push(it));
        requestList.push(this.fetchData(items, report, false, allItems));
      } else {
        this.setState({
          loadingAnalyse: false,
        });
      }
    });
    if (
      (this.props.reportItem?.item.data as IProviderSurveyReport)
        ?.taskReportList?.length
    ) {
      const mapRequest = { ...this.state.mapRequest };
      const mapResponse = { ...this.state.mapResponse };
      (
        this.props.reportItem?.item.data as IProviderSurveyReport
      ).taskReportList?.forEach((taskReport) => {
        mapRequest[taskReport.id!.toString()] = {
          data: {},
          order: taskReport.order,
        };
        mapResponse[taskReport.id!.toString()] = {};
      });
      this.setState({
        mapRequest,
        mapResponse,
      });
    }
    Promise.all(requestList).then(() => {
      this.setState({
        loadingAnalyse: false,
      });
    });
  };

  handleCollapseOrExpand = (type: 'collapse' | 'expand', reportId: number) => {
    let collapsedCharts = cloneDeep(this.state.collapsedCharts);
    if (type === 'collapse') {
      collapsedCharts.push(reportId);
    } else if (type === 'expand') {
      collapsedCharts = collapsedCharts.filter((id) => id !== reportId);
    }
    this.setState({ collapsedCharts });
  };

  render() {
    const {
      surveys,
      tags,
      users,
      providers,
      surveyId,
      reportItem,
      providerItems,
      surveyItem,
      embedded,
    } = this.props;
    const { display, editing, isOpen, allReports } = this.state;
    if (this.state.loading) {
      return (
        <div className="progress">
          <div className="indeterminate" />
        </div>
      );
    }
    let reports: ISurveyReports['data'] = {};
    if (surveyId) {
      reports = Object.values(surveys.reports.data)
        .filter((report: IReportView) => report.surveyId === surveyId)
        .reduce((box, itm) => {
          box[itm.id] = itm;
          return box;
        }, {});
    } else if (reportItem) {
      reports = this.getReports(reportItem, surveys);
    }

    const showDashboardGrid =
      display === ('Dashboard' as ReportViewType) && window.innerWidth > 500;
    const canUpdate =
      !!(surveyItem && surveyItem.editToken) ||
      !!(
        reportItem && (reportItem.item.data as IProviderSurveyReport).ownerToken
      );
    const canUpdateCheckpoint =
      !!(
        surveyItem &&
        (surveyItem.editToken ||
          (surveyItem.changeDataToken && !surveyItem.upstreamItemId))
      ) ||
      !!(
        reportItem &&
        (reportItem.item.data as IProviderSurveyReport).changeDataToken
      );
    return (
      <div
        className={!embedded ? 'modal-content' : ''}
        style={{
          padding: '10px 0px',
          overflowY: 'auto',
        }}
        ref={this.modalContent}
      >
        <div
          className="row"
          style={{ height: display === 'BSC' ? '100%' : '100%' }}
        >
          {display === 'List' && (
            <div
              style={{
                height: '100%',
                padding: '0px',
                float: languageDirection() === 'rtl' ? 'right' : 'left',
              }}
              className={`col ${display === 'List' && 'm4'}`}
            >
              <ChartSetting
                isOwnerOrSupport={this.props.isOwner}
                baseDate={this.props.baseDate}
                canUpdate={canUpdate}
                canUpdateCheckpoint={canUpdateCheckpoint}
                editing={this.state.editing}
                editType={this.state.editType}
                fields={this.state.editFields}
                loadAllReports={this.handleLoadAllReports}
                loadingAnalyse={this.state.loadingAnalyse}
                onChangeBaseDate={this.handleChangeBaseDate}
                onChangeField={this.handleChangeField}
                onChangeInput={this.handleChangeSettingInputs}
                onChangeTags={this.handleChangeReportTags}
                onChangeTimeScale={this.handleChangeTimeScale}
                onChangeTimeLength={this.handleChangeTimeLength}
                onChooseAReport={this.handleChooseAReport}
                onCopyReport={this.handleCopyAReport}
                onDeleteReport={this.handleDeleteReport}
                onPrepareReport={this.generateRequestForNewReport}
                onRemoveField={this.handleRemoveField}
                onSaveReport={this.handleSaveReport}
                onGenerateReport={this.handleGenerateReport}
                addOrUpdateTaskReport={this.handleAddOrUpdateTaskReport}
                providers={providers}
                providerTags={this.props.tags}
                reportName={this.state.reportName}
                reportTags={this.state.reportTags}
                reports={reports}
                saveReport={this.saveReport}
                searchedReportIds={this.state.searchedReportIds}
                // saveReportItem={this.handleSaveReportItem}
                savingReport={this.state.savingReport}
                selectedReport={this.state.selectedReport}
                splitOnTime={this.state.splitOnTime}
                dashboardHeight={this.state.dashboardHeight}
                dashboardLinks={this.state.dashboardLinks}
                onLinksChanged={this.handleLinksChanged}
                dashboardWidth={this.state.dashboardWidth}
                similarPastYearPeriodLength={
                  this.state.similarPastYearPeriodLength
                }
                totalTime={this.state.totalTime}
                survey={surveys.data[surveyId || this.state.relatedSurveyId!]}
                surveyReport={reportItem}
                surveyItem={surveyItem}
                timeScale={this.state.timeScale}
                timeLength={this.state.timeLength}
                viewOnlyInMainOwner={this.state.viewOnlyInMainOwner}
                oldVersion={this.state.oldVersion}
                updateSurveyReportItem={this.props.updateSurveyReportItem}
                deleteReportsInBulk={this.handleDeleteReportsInBulk}
                flag={this.state.flag}
                onChangeFlag={this.handleChangeFlag}
                company={this.props.company}
              />
            </div>
          )}
          <div
            className={`col s12 ${display !== 'List' ? 'm12' : 'm8'}`}
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              padding: this.props.embedded ? '0' : undefined,
            }}
          >
            {this.state.editing && this.state.editType !== 'task' && tags && (
              <ChartSeries
                onAddSeries={this.handleAddSeries}
                onChangeItem={this.handleChangeSeriesItem}
                onRemoveItem={this.handleRemoveSeriesItem}
                reportItem={reportItem}
                selectedReport={this.state.selectedReport}
                series={this.state.editSeries}
                surveyItem={surveys.data[surveyId!]?.data}
                tags={tags}
                onMoveItem={this.onMoveItem}
                editFields={this.state.editFields}
              />
            )}
            {!this.state.loadingAnalyse ? (
              <>
                {this.state.mapRequest &&
                  this.state.mapResponse &&
                  (() => {
                    const Container = showDashboardGrid
                      ? GridContainer
                      : ListContainer;

                    const mapRequest = this.state.mapRequest;
                    const mapResponse = this.state.mapResponse;
                    // const mapRequest = Object.values(
                    //   this.state.mapRequest || {}
                    // ).filter((el) =>
                    //   Object.entries(el?.data || {}).filter(
                    //     ([key, value]) => !Array.isArray(value[key])
                    //   )
                    // );
                    // const mapResponse = Object.values(
                    //   Object.values(this.state.mapResponse || {})
                    // ).filter((el) => !Array.isArray(el));
                    if (display === 'BSC')
                      return (
                        <Container>
                          <MergedChart
                            providerItems={providerItems}
                            tags={tags!}
                            mapRequest={mapRequest}
                            mapResponse={mapResponse}
                            reports={reports}
                            isPrinting={false}
                            surveys={surveys}
                            reportItem={reportItem}
                            reportItemMap={this.state.reportItemMap}
                            surveyItem={surveyItem}
                            providers={providers}
                            providerId={this.props.dataContext.providerId}
                          />
                        </Container>
                      );

                    return (
                      <Container
                        style={{
                          gridAutoRows: this.props.simple ? '190px' : undefined,
                        }}
                      >
                        {Object.entries(mapRequest)
                          .sort((a, b) => a[1].order - b[1].order)
                          .map((item) => {
                            const request = item[1];
                            const reportIdString = item[0];
                            const reportId = Number(reportIdString);
                            const analyse = mapResponse![reportIdString];
                            const report: IReportView =
                              reports[reportId] || allReports?.[reportIdString];
                            if (!report && reportId > 0) {
                              const taskReport = (
                                reportItem?.item.data as IProviderSurveyReport
                              ).taskReportList?.find(
                                (it) => it.id === reportId
                              );
                              if (
                                !taskReport ||
                                !this.props.company?.templates
                              ) {
                                return <></>;
                              }
                              const ItemBox = showDashboardGrid
                                ? GridReportBox
                                : ReportBox;
                              return (
                                <ItemBox
                                  style={{
                                    gridColumnEnd: `span ${Math.min(
                                      taskReport.dashboardWidth || 4,
                                      Math.floor(window.innerWidth / 185),
                                      6
                                    )}`,
                                    gridRowEnd: `span ${taskReport.dashboardHeight}`,
                                  }}
                                  key={reportId}
                                >
                                  <ViewerBox>
                                    <span>{taskReport.name}</span>
                                  </ViewerBox>
                                  <TaskReport
                                    readonly={true}
                                    inSurvey={true}
                                    defaultConfig={taskReport}
                                    height={
                                      (taskReport.dashboardHeight || 3) * 200 -
                                      150
                                    }
                                    templateId={taskReport.templateId}
                                    companyId={taskReport.companyId}
                                    activeProjectId={taskReport.projectId}
                                    parentId={taskReport.parentId}
                                    baseDate={this.props.baseDate}
                                    compactView={false}
                                    handleChangeProjectId={() => {
                                      /*do nothing*/
                                    }}
                                    fieldValues={taskReport.fieldMap}
                                  />
                                </ItemBox>
                              );
                            }
                            const checkpoints: ICheckPoint[] = Object.values(
                              surveys.checkPoints.data
                            ).filter((checkpoint: ICheckPoint) => {
                              return (
                                checkpoint.reportId === reportId ||
                                (checkpoint.surveyId === surveyId &&
                                  !checkpoint.reportId)
                              );
                            });
                            let series = this.state.viewSeries;
                            if (!editing && report) {
                              series = report.series;
                            }
                            const reportObj =
                              report || this.state.customReportForPrint;
                            if (
                              (this.props.selectedCategorizedTag &&
                                report.tags.includes(
                                  this.props.selectedCategorizedTag
                                )) ||
                              (this.props.selectedCategorizedTag ===
                                i18n.t('others', { ns: 'modals' }) &&
                                report.tags.length === 0) ||
                              !this.props.selectedCategorizedTag
                            )
                              return (
                                <SurveyChart
                                  reportOverride={
                                    this.state.reportOverrides[reportId] || {}
                                  }
                                  extraTagIds={this.props.extraTagIds}
                                  embedded={this.props.embedded}
                                  setOverride={(
                                    values: { key: string; value: any }[]
                                  ) => {
                                    this.setOverrides(reportId, values);
                                  }}
                                  display={display}
                                  key={reportId}
                                  groupId={this.state.cumulativePercentageKey}
                                  analyse={analyse}
                                  checkpoints={Object.values(checkpoints)}
                                  collapsedCharts={this.state.collapsedCharts}
                                  collapseOrExpand={this.handleCollapseOrExpand}
                                  // @ts-expect-error it's ok
                                  fields={
                                    report
                                      ? report.fields
                                      : this.state.viewFields
                                  }
                                  loading={this.state.loadingAnalyse}
                                  onChangeShowCheckPoints={
                                    this.handleChangeShowCheckPoints
                                  }
                                  onPrint={this.handlePrint(
                                    reportId.toString()
                                  )}
                                  onExport={this.handleExport(
                                    reportId.toString()
                                  )}
                                  reportItemMap={this.state.reportItemMap}
                                  providers={providers}
                                  report={report || reportObj}
                                  reportItem={reportItem}
                                  surveyItem={surveyItem}
                                  reportTitle={
                                    report?.name ||
                                    reportObj?.name ||
                                    this.state.reportName
                                    // ? report.get('name')
                                    // : this.state.reportName
                                  }
                                  requestQuery={request.data}
                                  series={series}
                                  showCheckPoints={this.state.showCheckPoints}
                                  survey={
                                    reportItem && report
                                      ? surveys.data[report.surveyId!]
                                      : surveys.data[surveyId!]
                                  }
                                  users={users}
                                  tags={tags!}
                                  timeScale={
                                    report
                                      ? report.time.timeScale
                                      : this.state.timeScale
                                  }
                                  timeLength={
                                    report
                                      ? report.time.timeLength
                                      : Number(this.state.timeLength)
                                  }
                                  isOpen={isOpen}
                                  setOpenItems={(isOpen) =>
                                    this.setState({
                                      isOpen: isOpen ?? new Set<string>(),
                                    })
                                  }
                                  saveReport={
                                    canUpdate ? this.saveReport : undefined
                                  }
                                  reportObjects={reports}
                                  simple={this.props.simple}
                                />
                              );
                            return null;
                          })}
                      </Container>
                    );
                  })()}
              </>
            ) : (
              <div style={{ margin: 'auto' }}>
                <LoadingCircleIndicator />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  private getReports(
    reportItem: IOpenItem,
    surveys: ISurveys
  ): ISurveyReports['data'] {
    let reports: ISurveyReports['data'] = {};
    const {
      item: { data },
    } = reportItem;
    const itemData = data as IProviderSurveyReport;
    const reportIds = itemData.reportIds ? [...itemData.reportIds] : [];
    if (itemData.surveyReportLinks) {
      itemData.surveyReportLinks?.forEach((reportLink) => {
        reportIds.push(reportLink.reportId);
      });
    }
    reports = Object.values(surveys.reports.data)
      .filter(
        (report: IReportView) =>
          reportIds.includes(report.id) ||
          this.state.searchedReportIds.includes(report.id)
      )
      .reduce((box, itm) => {
        box[itm.id] = cloneDeep(itm);
        itemData.surveyReportLinks?.forEach((reportLink) => {
          if (reportLink.reportId === itm.id)
            box[itm.id].surveyToken = reportLink.surveyReportToken;
        });
        return box;
      }, {});
    return reports;
  }
  _isUnmounted = false;
  componentWillUnmount() {
    this._isUnmounted = true;
  }
}

const mapStateToProps: MapStateToProps<StateToProps, OwnProps, RootState> = (
  state,
  props
) => {
  const surveys = state.survey;
  let providerId =
    props.reportItem?.item.data.providerId ??
    (props.surveyItem?.providerId || 0);
  if (props.dataContext.providerId) {
    providerId = Number(props.dataContext.providerId);
  }
  const tags = state.providers.tags[providerId];
  const providers = state.providers;
  const managingCompanyId =
    state.providers.map[providerId]?.data?.managingCompanyId;
  let company: ICompany | undefined = undefined;
  if (managingCompanyId) company = state.companies.data[managingCompanyId];
  const viewingVersion = props.surveyId
    ? surveys.data[props.surveyId].viewingVersion!
    : '';
  const survey = props.surveyId
    ? surveys.data[props.surveyId]?.data
    : undefined;
  const providerItems = state.providers.items[providerId]?.items;
  const users = state.users.map;
  const tasks = state.tasks.data;
  const thisUser = state.thisUser;
  const currentUserCompanyRoles: number[] = Object.keys(
    state.companies?.data?.[managingCompanyId!]?.members?.[
      Number(thisUser.userId)
    ]?.roles || {}
  )?.map((it) => Number(it));

  const isOwner =
    Boolean(
      Number(thisUser.userId) === state.providers.map[providerId]?.data?.user
    ) || Boolean(company && isCompanyOwnerOrSupport(thisUser, company));

  return {
    surveys,
    tags,
    providers,
    survey,
    viewingVersion,
    providerItems,
    users,
    tasks,
    company,
    currentUserCompanyRoles,
    isOwner,
    managingCompanyId,
    thisUserId: Number(thisUser.userId),
  };
};

const mapDispatchToProps: MapDispatchToProps<DispatchToProps, OwnProps> = (
  dispatch,
  ownProps
) => {
  return {
    // @ts-expect-error it's ok
    getReports: (surveyToken: string) => dispatch(getReports({ surveyToken })),
    requireReportsByIds: (reportIds: number[]) =>
      // @ts-expect-error it's ok
      dispatch(getReportByIds({ reportIds })),
    saveReport: (
      report: IReportView,
      isUpdate: boolean,
      surveyToken?: string,
      ownerToken?: string,
      reportToken?: string
    ) => {
      return appDispatch(
        saveReport({
          report,
          isUpdate,
          surveyToken,
          reportToken,
          ownerToken,
        })
      );
    },
    deleteReport: (
      reportId: number,
      surveyToken?: string,
      ownerToken?: string,
      reportToken?: string
    ) =>
      appDispatch(
        removeReport({ reportId, surveyToken, reportToken, ownerToken })
      ),
    exportResponses: (
      query: IExportResponsesQuery,
      surveyToken?: string,
      ownerToken?: string,
      reportToken?: string
    ) =>
      appDispatch(
        exportResponses({
          dataQuery: query,
          surveyToken,
          ownerToken,
          reportToken,
        })
      ),
    getAnalyse: (
      dataQuery: IAnalyticsQuery,
      surveyToken?: string,
      ownerToken?: string,
      reportToken?: string
    ) => {
      if ((ownProps.extraTagIds?.length ?? 0) > 0) {
        if (!dataQuery.filters) dataQuery.filters = [];
        dataQuery.filters = [
          ...dataQuery.filters,
          ...ownProps.extraTagIds!.map(
            (it) =>
              ({
                field: 'tags',
                values: [it],
                type: 'termFilter',
              }) as IQueryFilter
          ),
        ];
      }
      if (!surveyToken && !ownerToken && !reportToken) return Promise.reject();
      return appDispatch(
        getAnalyse({ dataQuery, surveyToken, ownerToken, reportToken })
      );
    },
    requireTags: (providerId: number) =>
      // @ts-expect-error it's ok
      dispatch(requireProviderTags({ providerId })),
    updateSurveyReportItem: (item: IProviderItemView) =>
      appDispatch(updateSurveyReport({ item })),
    getAllProviders: (providersId: number[]) =>
      appDispatch(getAllProviders({ providersId })),
    requireSeveralUser: (userIds: number[]) =>
      appDispatch(requireSeveralUser({ usersList: userIds })),
    requireAllCheckpoints: (
      reportsIds: number[],
      reportToken?: string,
      ownerToken?: string
    ) =>
      appDispatch(
        requireAllCheckpoints({ reportsIds, ownerToken, reportToken })
      ),
    requireCheckPoints: (
      surveyId?: number,
      reportId?: number,
      surveyToken?: string,
      ownerToken?: string,
      reportToken?: string
    ) =>
      dispatch(
        // @ts-expect-error it's ok
        requireCheckPoints({
          surveyId,
          reportId,
          surveyToken,
          ownerToken,
          reportToken,
        })
      ),
    requireAllSurveys: (surveysTokens: string[]) =>
      appDispatch(requireAllSurveys({ surveyTokens: surveysTokens })),
    cumulativePercentageReset: (groupId: string) =>
      appDispatch(cumulativePercentageReset({ groupId })),
    generateReport: (
      questionId: number | undefined,
      options: any,
      surveyToken: string
    ) =>
      appDispatch(
        generateReports({ questionId, config: options, surveyToken })
      ),
    getDocuments: (itemIds: number[]) =>
      appDispatch(requireProviderDocuments({ documentIds: itemIds })),
    getCompany: (companyId: number) =>
      appDispatch(
        getCompanyHandler({
          companyId,
          templateStub: false,
          companyStub: false,
          projectStub: true,
          taskStub: false,
        })
      ),
    getTaskReport: (
      companyId: number,
      templateReport: ITemplateReportMark,
      startDate: number,
      toDate: number,
      projectId: NumberOrNull,
      parentId: NumberOrNull,
      templateId: number,
      assigneeId: NumberOrNull,
      splitMonths: number,
      splitField: StringOrNull,
      fieldValues: { [key: string]: string }
    ) =>
      appDispatch(
        getTaskReport({
          companyId,
          templateReport,
          startDate,
          toDate,
          projectId,
          parentId,
          templateId,
          assigneeId,
          splitMonths,
          splitField,
          fieldValues,
        })
      ),
  };
};

export default withDataContext(
  connect(mapStateToProps, mapDispatchToProps)(ChartViewer)
);
